// Tipos para el módulo de Impuestos

export type EstadoImpuesto = 'pendiente' | 'declarado' | 'pagado' | 'vencido' | 'plan_pago'

export type TipoImpuesto =
  | 'IVA'
  | 'Ganancias'
  | 'IIBB'
  | 'Monotributo'
  | 'Autónomos'
  | 'Bienes Personales'
  | 'Ingresos Brutos CM'
  | 'Otro'

export interface ImpuestoCliente {
  id: string
  cliente_id: string
  tipo_impuesto: string
  periodo: string
  monto_calculado: number | null
  monto_declarado: number | null
  monto_pagado: number | null
  fecha_vencimiento: string
  fecha_pago: string | null
  estado: EstadoImpuesto
  vep_numero: string | null
  vep_fecha: string | null
  vep_monto: number | null
  vep_vencimiento: string | null
  vep_pagado: boolean
  ddjj_id: string | null
  vencimiento_id: string | null
  observaciones: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface ImpuestoClienteConDatos extends ImpuestoCliente {
  cliente_nombre: string
  cliente_cuit: string
  cliente_condicion: string | null
  ddjj_fecha: string | null
  ddjj_numero: string | null
  saldo_pendiente: number
  dias_para_vencer: number
}

export interface FiltrosImpuestos {
  cliente_id?: string
  tipo_impuesto?: string
  periodo?: string
  estado?: EstadoImpuesto
  desde?: string
  hasta?: string
  solo_pendientes?: boolean
  solo_vencidos?: boolean
}

export interface RegistrarVEPData {
  vep_numero: string
  vep_fecha: string
  vep_monto: number
  vep_vencimiento?: string
}

export interface RegistrarPagoData {
  fecha_pago: string
  monto_pagado: number
  observaciones?: string
}

export interface ResumenImpuestosCliente {
  cliente_id: string
  cliente_nombre: string
  total_pendiente: number
  total_pagado: number
  impuestos_vencidos: number
  impuestos_por_vencer: number
  proximos_vencimientos: ImpuestoClienteConDatos[]
}

export interface EstadisticasImpuestos {
  total_impuestos: number
  total_pendientes: number
  total_vencidos: number
  total_pagados: number
  monto_total_pendiente: number
  monto_total_pagado: number
  por_tipo: {
    tipo: string
    cantidad: number
    monto_pendiente: number
  }[]
}

// Configuración visual de estados
export const ESTADO_IMPUESTO_CONFIG: Record<EstadoImpuesto, { label: string; color: string; bgColor: string }> = {
  pendiente: { label: 'Pendiente', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  declarado: { label: 'Declarado', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  pagado: { label: 'Pagado', color: 'text-green-700', bgColor: 'bg-green-100' },
  vencido: { label: 'Vencido', color: 'text-red-700', bgColor: 'bg-red-100' },
  plan_pago: { label: 'Plan de Pago', color: 'text-purple-700', bgColor: 'bg-purple-100' },
}

// Tipos de impuestos comunes
export const TIPOS_IMPUESTO: { value: TipoImpuesto; label: string }[] = [
  { value: 'IVA', label: 'IVA' },
  { value: 'Ganancias', label: 'Ganancias' },
  { value: 'IIBB', label: 'Ingresos Brutos' },
  { value: 'Monotributo', label: 'Monotributo' },
  { value: 'Autónomos', label: 'Autónomos' },
  { value: 'Bienes Personales', label: 'Bienes Personales' },
  { value: 'Ingresos Brutos CM', label: 'IIBB Convenio Multilateral' },
  { value: 'Otro', label: 'Otro' },
]

// ===========================================
// TIPOS: Catalogo de Impuestos a Liquidar
// ===========================================

export type CategoriaImpuestoCatalogo =
  | 'iva'
  | 'ganancias'
  | 'iibb'
  | 'patrimonial'      // Existente - mantener para compatibilidad
  | 'bienes'           // AFIP: Bienes Personales
  | 'laboral'          // Existente - mantener para compatibilidad
  | 'autonomos'        // AFIP: Régimen de Autónomos
  | 'suss'             // AFIP: Seguridad Social (aportes, contribuciones)
  | 'retenciones'      // AFIP: Retenciones y Percepciones
  | 'municipal'
  | 'monotributo'
  | 'otros'

export type ColorImpuesto =
  | 'gray' | 'slate'
  | 'red' | 'rose' | 'orange' | 'amber' | 'yellow'
  | 'green' | 'emerald' | 'teal' | 'cyan'
  | 'blue' | 'sky' | 'indigo' | 'violet' | 'purple'

// Registro del catalogo
export interface CatalogoImpuesto {
  id: string
  organizacion_id: string | null
  codigo: string
  nombre: string
  nombre_corto: string | null
  categoria: CategoriaImpuestoCatalogo
  jurisdiccion: string | null
  color: ColorImpuesto
  orden: number
  activo: boolean
  is_global: boolean
  source: 'system' | 'manual' | 'afip_sync'
  created_at: string
  updated_at: string
}

// Impuesto asignado a un cliente (para JSONB)
export interface ImpuestoLiquidar {
  impuesto_id: string    // ID del catalogo
  codigo: string         // Codigo para referencia rapida
  notas?: string         // Notas especificas del cliente
  // Fecha de inscripción (AAAA-MM-01) derivada del período del padrón A5. ORR-152.
  fecha_inscripcion?: string | null
}

// Para mostrar en UI (con datos del catalogo)
export interface ImpuestoLiquidarCompleto extends ImpuestoLiquidar {
  nombre: string
  nombre_corto: string
  color: ColorImpuesto
  categoria: CategoriaImpuestoCatalogo
}

// Mapeo de colores a clases de Tailwind
export const COLORES_IMPUESTO: Record<ColorImpuesto, { bg: string; text: string; border: string }> = {
  gray:    { bg: 'bg-gray-100 dark:bg-gray-800',     text: 'text-gray-700 dark:text-gray-300',     border: 'border-gray-300 dark:border-gray-600' },
  slate:   { bg: 'bg-slate-100 dark:bg-slate-800',   text: 'text-slate-700 dark:text-slate-300',   border: 'border-slate-300 dark:border-slate-600' },
  red:     { bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-700 dark:text-red-300',       border: 'border-red-300 dark:border-red-700' },
  rose:    { bg: 'bg-rose-100 dark:bg-rose-900/30',  text: 'text-rose-700 dark:text-rose-300',     border: 'border-rose-300 dark:border-rose-700' },
  orange:  { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-700' },
  amber:   { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300',  border: 'border-amber-300 dark:border-amber-700' },
  yellow:  { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-700' },
  green:   { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300',  border: 'border-green-300 dark:border-green-700' },
  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-700' },
  teal:    { bg: 'bg-teal-100 dark:bg-teal-900/30',  text: 'text-teal-700 dark:text-teal-300',     border: 'border-teal-300 dark:border-teal-700' },
  cyan:    { bg: 'bg-cyan-100 dark:bg-cyan-900/30',  text: 'text-cyan-700 dark:text-cyan-300',     border: 'border-cyan-300 dark:border-cyan-700' },
  blue:    { bg: 'bg-blue-100 dark:bg-blue-900/30',  text: 'text-blue-700 dark:text-blue-300',     border: 'border-blue-300 dark:border-blue-700' },
  sky:     { bg: 'bg-sky-100 dark:bg-sky-900/30',    text: 'text-sky-700 dark:text-sky-300',       border: 'border-sky-300 dark:border-sky-700' },
  indigo:  { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-300 dark:border-indigo-700' },
  violet:  { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-300', border: 'border-violet-300 dark:border-violet-700' },
  purple:  { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
}

// Categorias con labels - Alineado a AFIP/ARCA
export const CATEGORIAS_IMPUESTO_CATALOGO: Record<CategoriaImpuestoCatalogo, string> = {
  iva: 'IVA',
  ganancias: 'Ganancias',
  iibb: 'Ingresos Brutos',
  patrimonial: 'Patrimoniales',      // Legacy - mantener para datos existentes
  bienes: 'Bienes Personales',       // AFIP
  laboral: 'Laborales',              // Legacy - mantener para datos existentes
  autonomos: 'Autónomos',            // AFIP
  suss: 'Seguridad Social',          // AFIP (SUSS)
  retenciones: 'Retenciones/Percepciones', // AFIP
  municipal: 'Municipales',
  monotributo: 'Monotributo',
  otros: 'Otros',
}
